/**
 * PGLP | MONTE HERMOM
 * COMPONENTE: PrazoDetalhesModal
 * ------------------------------------------------------------
 * Modal com breakdown completo do prazo operacional.
 * Abre ao clicar na PrazoBar.
 * ============================================================
 */

import React from 'react';
import {
  X, Clock, TrendingDown, Calendar, Activity,
  AlertTriangle, ShieldAlert, CheckCircle2, XCircle,
  Timer, PieChart, Target,
} from 'lucide-react';

const CORES = {
  NORMAL:    { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', label: 'Normal' },
  ATENCAO:   { bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   text: 'text-amber-400',   label: 'Atenção' },
  RISCO:     { bg: 'bg-orange-500/10',  border: 'border-orange-500/30',  text: 'text-orange-400',  label: 'Risco' },
  CRITICO:   { bg: 'bg-rose-500/10',    border: 'border-rose-500/30',    text: 'text-rose-400',    label: 'Crítico' },
  CONSUMIDO: { bg: 'bg-slate-500/10',   border: 'border-slate-500/30',   text: 'text-slate-400',   label: 'Consumido' },
};

export default function PrazoDetalhesModal({ estado, onFechar }) {
  if (!estado) return null;

  const cor = CORES[estado.nivel_risco] || CORES.NORMAL;

  const pctConsumido = estado.horas_totais > 0
    ? Math.round((estado.horas_consumidas / estado.horas_totais) * 100)
    : 0;

  const pctMargem = estado.horas_totais > 0
    ? Math.round((estado.margem_atual / estado.horas_totais) * 100)
    : 0;

  const dataLimiteFmt = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return d; }
  };

  const dataInicioFmt = dataLimiteFmt(estado.data_inicio);

  return (
    <div className="fixed inset-0 z-[400] bg-slate-950/95 backdrop-blur-xl flex items-center justify-center p-4">
      <div className="bg-slate-900 w-full max-w-3xl rounded-3xl border border-white/10 shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">

        {/* HEADER */}
        <div className="bg-slate-950 p-5 flex justify-between items-center border-b border-white/5 shrink-0">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${cor.bg} ${cor.border} border`}>
              <Activity size={18} className={cor.text} />
            </div>
            <div>
              <h3 className="text-white text-[14px] font-black uppercase italic tracking-tight leading-none">
                Prazo Operacional · Ciclo {estado.ciclo}
              </h3>
              <p className="text-[9px] text-slate-500 font-black uppercase tracking-widest mt-1">
                Breakdown completo do consumo
              </p>
            </div>
          </div>
          <button
            onClick={onFechar}
            className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-all"
          >
            <X size={16} />
          </button>
        </div>

        {/* BODY */}
        <div className="p-5 space-y-4 overflow-y-auto flex-1">

          {/* STATUS GERAL */}
          <div className={`rounded-2xl border px-5 py-4 ${cor.bg} ${cor.border}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[8px] font-black text-slate-500 uppercase tracking-[0.3em] mb-1">
                  Nível de Risco
                </p>
                <p className={`text-[18px] font-black uppercase italic tracking-tight ${cor.text}`}>
                  {cor.label}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest">
                  Data Limite
                </p>
                <p className="text-[14px] font-mono font-bold text-white">
                  {dataLimiteFmt(estado.data_limite_atual)}
                </p>
              </div>
            </div>
          </div>

          {/* GRID DE MÉTRICAS */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">

            <CardMetrica
              icone={Clock}
              cor="text-amber-400"
              label="Horas Totais"
              valor={`${estado.horas_totais}h`}
              sub="Ciclo completo"
            />

            <CardMetrica
              icone={Timer}
              cor="text-rose-400"
              label="Consumidas"
              valor={`${estado.horas_consumidas}h`}
              sub={`${pctConsumido}% do total`}
            />

            <CardMetrica
              icone={PieChart}
              cor="text-emerald-400"
              label="Restantes"
              valor={`${estado.horas_restantes}h`}
              sub={`${100 - pctConsumido}% disponível`}
            />

            <CardMetrica
              icone={TrendingDown}
              cor={cor.text}
              label="Margem"
              valor={`${estado.margem_atual}h`}
              sub={`${pctMargem}% de folga`}
            />

          </div>

          {/* BARRA VISUAL DE CONSUMO */}
          <div className="bg-slate-950 rounded-2xl border border-white/5 p-5 space-y-3">
            <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">
              Composição do Prazo
            </p>

            <div className="w-full h-6 rounded-full bg-slate-900 overflow-hidden flex border border-white/5">
              {estado.horas_consumidas > 0 && (
                <div
                  className="h-full bg-rose-500/70 flex items-center justify-center transition-all"
                  style={{ width: `${pctConsumido}%` }}
                >
                  {pctConsumido >= 15 && (
                    <span className="text-[8px] font-black text-white">
                      {pctConsumido}% consumido
                    </span>
                  )}
                </div>
              )}
              {estado.horas_reservadas_futuras > 0 && (
                <div
                  className="h-full bg-amber-500/40 flex items-center justify-center transition-all"
                  style={{
                    width: `${Math.round((estado.horas_reservadas_futuras / estado.horas_totais) * 100)}%`,
                  }}
                >
                  <span className="text-[8px] font-black text-white/90">
                    Reservas
                  </span>
                </div>
              )}
              {estado.margem_atual > 0 && (
                <div
                  className="h-full bg-emerald-500/60 flex items-center justify-center transition-all"
                  style={{ width: `${pctMargem}%` }}
                >
                  {pctMargem >= 8 && (
                    <span className="text-[8px] font-black text-white">
                      Margem
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-between text-[9px] font-black uppercase tracking-widest">
              <span className="text-rose-400">● Consumido: {estado.horas_consumidas}h</span>
              <span className="text-amber-400">● Reservado: {estado.horas_reservadas_futuras}h</span>
              <span className="text-emerald-400">● Margem: {estado.margem_atual}h</span>
            </div>
          </div>

          {/* DETALHES TEMPORAIS */}
          <div className="bg-slate-950 rounded-2xl border border-white/5 p-5 space-y-3">
            <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">
              Marcos Temporais
            </p>

            <div className="grid grid-cols-2 gap-4 text-[11px]">
              <div>
                <p className="text-slate-500 text-[8px] font-black uppercase tracking-widest mb-1">
                  <Calendar size={10} className="inline mr-1" />
                  Início
                </p>
                <p className="font-mono font-bold text-white">{dataInicioFmt}</p>
              </div>
              <div>
                <p className="text-slate-500 text-[8px] font-black uppercase tracking-widest mb-1">
                  <Target size={10} className="inline mr-1" />
                  Limite Atual
                </p>
                <p className="font-mono font-bold text-white">
                  {dataLimiteFmt(estado.data_limite_atual)}
                </p>
              </div>
              <div>
                <p className="text-slate-500 text-[8px] font-black uppercase tracking-widest mb-1">
                  <Activity size={10} className="inline mr-1" />
                  Horas Trabalhadas
                </p>
                <p className="font-mono font-bold text-white">{estado.horas_trabalhadas}h</p>
              </div>
              <div>
                <p className="text-slate-500 text-[8px] font-black uppercase tracking-widest mb-1">
                  <PieChart size={10} className="inline mr-1" />
                  Reservas Próximas
                </p>
                <p className="font-mono font-bold text-white">
                  {estado.horas_reservadas_proximas != null
                    ? `${estado.horas_reservadas_proximas}h`
                    : '—'}
                </p>
              </div>
            </div>
          </div>

          {/* FLAGS */}
          {(estado.em_suspensao === 1 ||
            estado.possui_prorrogacao === 1 ||
            estado.possui_impossibilidade_acesso === 1 ||
            estado.possui_acesso_parcial === 1) && (
            <div className="bg-slate-950 rounded-2xl border border-white/5 p-4 space-y-2">
              <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-2">
                Alertas Ativos
              </p>

              {estado.em_suspensao === 1 && (
                <LinhaAlerta
                  icone={AlertTriangle}
                  cor="text-amber-400"
                  texto="Suspensão JURIDICA ativa — prazo pausado"
                />
              )}
              {estado.possui_prorrogacao === 1 && (
                <LinhaAlerta
                  icone={CheckCircle2}
                  cor="text-emerald-400"
                  texto={`${estado.qtd_prorrogacoes} prorrogação(ões) concedida(s)`}
                />
              )}
              {estado.possui_impossibilidade_acesso === 1 && (
                <LinhaAlerta
                  icone={XCircle}
                  cor="text-rose-400"
                  texto="Impossibilidade de acesso registrada"
                />
              )}
              {estado.possui_acesso_parcial === 1 && (
                <LinhaAlerta
                  icone={ShieldAlert}
                  cor="text-orange-400"
                  texto="Acesso parcial registrado"
                />
              )}
            </div>
          )}

        </div>

        {/* FOOTER */}
        <div className="p-5 border-t border-white/5 bg-slate-950/50 flex justify-end shrink-0">
          <button
            onClick={onFechar}
            className="px-6 py-3 rounded-xl bg-white/5 text-slate-300 hover:bg-white/10 text-[10px] font-black uppercase tracking-widest transition-all"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   SUB-COMPONENTES
   ============================================================ */

function CardMetrica({ icone: Icone, cor, label, valor, sub }) {
  return (
    <div className="bg-slate-950 rounded-2xl border border-white/5 p-4 space-y-2">
      <div className="flex items-center gap-2">
        <Icone size={12} className={cor} />
        <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest">
          {label}
        </p>
      </div>
      <p className={`text-[18px] font-mono font-black ${cor} leading-none`}>
        {valor}
      </p>
      <p className="text-[9px] text-slate-500 font-bold uppercase tracking-wide">
        {sub}
      </p>
    </div>
  );
}

function LinhaAlerta({ icone: Icone, cor, texto }) {
  return (
    <div className="flex items-center gap-2">
      <Icone size={12} className={cor} />
      <p className="text-[10px] font-bold text-slate-300">{texto}</p>
    </div>
  );
}